/**
 * Kennungen — UUIDv7, selbst erzeugt.
 *
 * v7 trägt die Zeit in den ersten 48 Bit. Das hat zwei Folgen, die für uns
 * zählen: Kennungen sind nach Entstehungszeit sortierbar, und
 * Datenbank-Indizes fragmentieren nicht, wie es bei v4 der Fall ist.
 *
 * Ohne Abhängigkeit, weil weder Node noch der Browser v7 mitbringen und jede
 * neue Abhängigkeit eine Entscheidung gegen „ein Server, eine Datenbank" ist.
 * Zwanzig Zeilen sind billiger als ein Paket, das altert.
 *
 * Der Zufall kommt aus der Web-Crypto-API (`crypto.getRandomValues`) und nicht
 * aus `node:crypto`. Dieses Paket wird auch von `apps/web` importiert — ein
 * Node-Import stünde im Browser-Bundle und bräche den Bau.
 *
 * Monotonie innerhalb derselben Millisekunde ist ausdrücklich geregelt: Ohne
 * sie wäre die Sortierung bei schnell aufeinanderfolgenden Einfügungen zufällig
 * — und genau darauf verlässt sich der Ereignisstrom.
 */
let letzteMillisekunde = -1;
let zaehler = 0;
/**
 * Eine neue UUIDv7.
 *
 * Aufbau nach RFC 9562:
 *   48 Bit Zeitstempel · 4 Bit Fassung (7) · 12 Bit Zähler ·
 *    2 Bit Variante (0b10) · 62 Bit Zufall
 */
export function neueKennung(jetzt = Date.now()) {
    if (jetzt === letzteMillisekunde) {
        zaehler += 1;
        // 12 Bit sind voll — auf die nächste Millisekunde ausweichen, statt
        // überzulaufen und die Sortierung zu brechen.
        if (zaehler > 0xfff) {
            letzteMillisekunde = jetzt + 1;
            zaehler = 0;
            return neueKennung(letzteMillisekunde);
        }
    }
    else {
        letzteMillisekunde = jetzt;
        zaehler = 0;
    }
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    // 48 Bit Zeit, big endian
    bytes[0] = (jetzt / 2 ** 40) & 0xff;
    bytes[1] = (jetzt / 2 ** 32) & 0xff;
    bytes[2] = (jetzt / 2 ** 24) & 0xff;
    bytes[3] = (jetzt / 2 ** 16) & 0xff;
    bytes[4] = (jetzt / 2 ** 8) & 0xff;
    bytes[5] = jetzt & 0xff;
    // Fassung 7 in den oberen vier Bit von Byte 6, darunter der Zähler
    bytes[6] = 0x70 | ((zaehler >> 8) & 0x0f);
    bytes[7] = zaehler & 0xff;
    // Variante 0b10 in den oberen zwei Bit von Byte 8
    bytes[8] = 0x80 | (bytes[8] & 0x3f);
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return [
        hex.slice(0, 8),
        hex.slice(8, 12),
        hex.slice(12, 16),
        hex.slice(16, 20),
        hex.slice(20, 32),
    ].join("-");
}
/** Der Zeitpunkt, den eine v7-Kennung mit sich trägt. */
export function entstandenAm(kennung) {
    const roh = kennung.replace(/-/g, "");
    if (roh.length !== 32)
        return null;
    if (roh[12] !== "7")
        return null;
    const millisekunden = Number.parseInt(roh.slice(0, 12), 16);
    return Number.isFinite(millisekunden) ? new Date(millisekunden) : null;
}
const UUID_MUSTER = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export function istKennung(wert) {
    return UUID_MUSTER.test(wert);
}
//# sourceMappingURL=kennungen.js.map